import {
    Fragment,
    useState,
    useEffect,
    useRef
} from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    useTheme,
    Paper
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getSpecificCollection, getCollections } from "../../store/collection/collectionSlice";
import { getUserData } from "../../store/user/userSlice";
import Navbar from "../Navbar/Navbar";
import PrimaryButton from "../Common/PrimaryButton/PrimaryButton";
import ItemThumbnail from "../../legacy-components/item-thumbnail/item-thumbnail";
import useIsMobile from "../../hooks/media/useIsMobile";
import useLoginModal from "../../hooks/state/useLoginModal";
import useLoadingModal from "../../hooks/state/useLoadingModal";
import axios from "axios";
import apiRoutes from "../../constants/api-routes";

export default function SeekingCurators({ collectionId, topic }) {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const { userId } = useSelector(getUserData);
    const collection = useSelector(getCollections)[0];
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(true);
    const { AccountModalHandler } = useLoginModal();
    const { loading: loadingModal, setLoading: setLoadingModal, LoadingModal } = useLoadingModal();
    const [success, setSuccess] = useState(false);
    const emailFormRef = useRef(null);

    useEffect(() => {
        if (collectionId) {
            setLoading(true);
            dispatch(getSpecificCollection({ collectionId, userId })).then(() => setLoading(false));
        }
    }, [collectionId, userId, dispatch]);

    const handleSubmit = () => {
        if (!email) return;
        setLoadingModal(true);
        axios.get(apiRoutes.contact, {
            params: {
                name: "Interested Curator",
                email,
                category: "Curator Request",
                userMessage: `Interested in curating or contributing to collection: ${collectionTitle} (ID: ${collectionId})${topic ? ` for topic: ${topic}` : ""}`
            }
        }).then(() => {
            setSuccess(true);
        }).finally(() => {
            setLoadingModal(false);
        });
    };



    // Use collection data if available, fallback to generic
    const collectionTitle = collection?.collectionTitle || "this collection";

    return (
        <Fragment>
            <Navbar />
            <Container maxWidth="lg" sx={{ py: 6, mt: 8 }}>
                {/* Main Headline Section */}
                {loading ? (
                    <Box sx={{ textAlign: "center", py: 10 }}>
                        <Typography variant="h4" color="text.secondary">Loading collection info...</Typography>
                    </Box>
                ) : (
                    <Paper
                        elevation={3}
                        sx={{
                            mb: 0,
                            px: { xs: 2, sm: 6 },
                            py: { xs: 7, sm: 10 },
                            borderRadius: 5,
                            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                            boxShadow: "0 8px 32px rgba(60,60,60,0.10)",
                            textAlign: "center",
                            maxWidth: 900,
                            mx: "auto",
                            position: "relative"
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 900,
                                mb: 2,
                                fontSize: isMobile ? "2.3rem" : "3.5rem",
                                letterSpacing: -1,
                                lineHeight: 1.1
                            }}
                        >
                            Become a <Box component="span" sx={{ color: theme.palette.secondary.main, display: "inline", fontWeight: 900 }}>leader</Box> on Slyke for {topic ? topic : "this topic"}!
                        </Typography>
                        <PrimaryButton
                            text="Learn More"
                            onClick={() => {
                                if (emailFormRef.current) {
                                    emailFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                                }
                            }}
                            sx={{
                                mt: 5,
                                fontSize: isMobile ? "1.1rem" : "1.25rem",
                                fontWeight: 700,
                                px: 5,
                                py: 2,
                                borderRadius: 3,
                                boxShadow: "0 4px 16px rgba(60,60,60,0.10)",
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                color: "white",
                                transition: "background 0.3s, box-shadow 0.3s",
                                "&:hover": {
                                    background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                    boxShadow: "0 8px 32px rgba(60,60,60,0.15)"
                                }
                            }}
                        />
                    </Paper>
                )}

                {/* Featured Collection Preview */}
                {collection && collection.items && collection.items.length > 0 && (
                    <Box sx={{ mb: 8, mt: 6 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: "center",
                                fontWeight: "700",
                                mb: 4,
                                fontSize: isMobile ? "1.5rem" : "2rem"
                            }}
                        >
                            Featured Collection:
                            <span style={{ color: "#ff9800" }}>
                                &nbsp;{collectionTitle}
                            </span>
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                overflowX: "auto",
                                gap: 2,
                                px: isMobile ? 1 : 2,
                                pb: 2,
                                "&::-webkit-scrollbar": {
                                    height: 6,
                                },
                                "&::-webkit-scrollbar-track": {
                                    background: "rgba(0,0,0,0.1)",
                                    borderRadius: 3,
                                },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "rgba(0,0,0,0.3)",
                                    borderRadius: 3,
                                },
                            }}
                        >
                            {collection.items.slice(0, 6).map((item, index) => (
                                <Box key={`preview-item-${index}`} sx={{ flexShrink: 0 }}>
                                    <ItemThumbnail
                                        item={item}
                                        collectionId={collection.collectionId}
                                        itemIndex={index}
                                        editState={false}
                                        isRejected={false}
                                        refresh={() => { }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Section Divider: Ready to get involved? */}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    my: { xs: 6, sm: 8 },
                    mx: "auto",
                    maxWidth: 600,
                }}>
                    <Box sx={{ flex: 1, height: 2, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)", opacity: 0.18, borderRadius: 2 }} />
                    <Typography
                        variant="h6"
                        sx={{
                            mx: 3,
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            letterSpacing: 0.5,
                            fontSize: isMobile ? "1.08rem" : "1.18rem",
                            whiteSpace: "nowrap",
                            background: "rgba(255,255,255,0.85)",
                            px: 2,
                            borderRadius: 2,
                            boxShadow: "0 2px 8px 0 rgba(60,60,60,0.04)"
                        }}
                    >
                        Get Involved
                    </Typography>
                    <Box sx={{ flex: 1, height: 2, background: "linear-gradient(90deg, #764ba2 0%, #667eea 100%)", opacity: 0.18, borderRadius: 2 }} />
                </Box>
                {/* Email Collection Form - always visible */}
                <Box sx={{ mb: 10, mt: 4 }} ref={emailFormRef}>
                    {success ? (
                        <Paper elevation={0} sx={{
                            background: "rgba(255,255,255,0.65)",
                            backdropFilter: "blur(12px)",
                            borderRadius: 6,
                            maxWidth: 420,
                            mx: "auto",
                            width: "100%",
                            boxShadow: "0 8px 32px 0 rgba(60,60,60,0.13)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            px: { xs: 2.5, sm: 5 },
                            py: { xs: 4, sm: 6 },
                            position: "relative",
                        }}>
                            <Box sx={{
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 60,
                                height: 5,
                                borderRadius: 3,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                mb: 3,
                            }} />
                            <Typography variant="h5" sx={{ mt: 3, mb: 2, fontWeight: 800, color: theme.palette.text.primary, textAlign: "center", letterSpacing: 0.2 }}>
                                Thank you for your interest!
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary, textAlign: "center", fontWeight: 500 }}>
                                We received your submission and will reach out soon.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper elevation={0} sx={{
                            background: "rgba(255,255,255,0.65)",
                            backdropFilter: "blur(12px)",
                            borderRadius: 6,
                            maxWidth: 420,
                            mx: "auto",
                            width: "100%",
                            boxShadow: "0 8px 32px 0 rgba(60,60,60,0.13)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            px: { xs: 2.5, sm: 5 },
                            py: { xs: 4, sm: 6 },
                            position: "relative",
                        }}>
                            <Box sx={{
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 60,
                                height: 5,
                                borderRadius: 3,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                mb: 3,
                            }} />
                            <Typography variant="h5" sx={{ mt: 3, mb: 1.5, fontWeight: 800, color: theme.palette.text.primary, textAlign: "center", letterSpacing: 0.2 }}>
                                Be <span style={{ color: theme.palette.secondary.main }}>Slyke&apos;s Community Expert</span> on {topic ? topic.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : "this topic"}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 3, mb: 2, fontWeight: 400, color: theme.palette.text.primary, textAlign: "center", letterSpacing: 0.2 }}>
                                Collaborate with others to curate {topic ? topic : "this topic"} knowledge!
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="your.email@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                InputProps={{
                                    sx: {
                                        background: "rgba(255,255,255,0.92)",
                                        borderRadius: 3,
                                        fontSize: "1.08rem",
                                        px: 1.5,
                                        py: 1,
                                        boxShadow: "0 1px 4px 0 rgba(60,60,60,0.04)",
                                        border: "1.5px solid #e0e7ef",
                                        "&:hover": {
                                            borderColor: theme.palette.primary.main,
                                            background: "rgba(255,255,255,1)",
                                        },
                                        "&.Mui-focused": {
                                            borderColor: theme.palette.primary.main,
                                            background: "rgba(255,255,255,1)",
                                        },
                                    }
                                }}
                                sx={{ mb: 3, borderRadius: 3 }}
                            />
                            <PrimaryButton
                                text={loadingModal ? "Submitting..." : "Yes, tell me more!"}
                                onClick={handleSubmit}
                                disabled={loadingModal}
                                sx={{
                                    minWidth: "220px",
                                    py: 1.7,
                                    px: 4,
                                    fontSize: "1.13rem",
                                    borderRadius: 999,
                                    fontWeight: 700,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    color: "white",
                                    boxShadow: "0 4px 16px rgba(60,60,60,0.10)",
                                    transition: "background 0.3s, box-shadow 0.3s",
                                    "&:hover": {
                                        background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                        boxShadow: "0 8px 32px rgba(60,60,60,0.15)"
                                    }
                                }}
                            />
                        </Paper>
                    )}
                </Box>

                {/* Creative Slyke Description */}
                <Box sx={{
                    mt: 12,
                    mb: 6,
                    textAlign: "center",
                    position: "relative",
                    "&::before": {
                        content: "\"\"",
                        position: "absolute",
                        top: "50%",
                        left: 0,
                        right: 0,
                        height: "1px",
                        background: "linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)",
                        transform: "translateY(-50%)"
                    }
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            background: "rgba(255,255,255,0.8)",
                            backdropFilter: "blur(10px)",
                            borderRadius: 4,
                            p: { xs: 3, sm: 4 },
                            maxWidth: 800,
                            mx: "auto",
                            position: "relative",
                            border: "1px solid rgba(102, 126, 234, 0.1)",
                            boxShadow: "0 4px 20px rgba(60,60,60,0.08)"
                        }}
                    >
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            gap: 1
                        }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                animation: "pulse 2s infinite"
                            }} />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    letterSpacing: 0.5,
                                    fontSize: isMobile ? "1.1rem" : "1.25rem"
                                }}
                            >
                                What is Slyke?
                            </Typography>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                                animation: "pulse 2s infinite 1s"
                            }} />
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: isMobile ? "1.05rem" : "1.15rem",
                                lineHeight: 1.7,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                fontStyle: "italic"
                            }}
                        >
                            Slyke is a crowdsourced platform for building wiki-style Collections of links, images, and videos on super-specific topics. Think{" "}
                            <Box component="span" sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent"
                            }}>
                                Reddit meets Wikipedia in Pinterest style
                            </Box>
                            !
                        </Typography>
                    </Paper>
                </Box>
            </Container>
            <AccountModalHandler isMobile={isMobile} />
            <LoadingModal open={loadingModal} onClose={() => setLoadingModal(false)} />
        </Fragment>
    );
}

const style = document.createElement("style");
style.innerHTML = `
    @keyframes bounce { 
        0% { transform: translateY(0); } 
        100% { transform: translateY(-6px); } 
    }
    @keyframes pulse { 
        0%, 100% { opacity: 1; transform: scale(1); } 
        50% { opacity: 0.7; transform: scale(1.1); } 
    }
`;
document.head.appendChild(style); 